/**
 * useSessionStream — WebSocket-based real-time session state for dynapp.
 *
 * Connects to the flux-service session WebSocket endpoint. Falls back to REST
 * polling if the initial WebSocket connection fails.
 */
import { useEffect, useRef, useState } from 'react';
import { auth, FLUX_API_BASE } from './firebase';
import type { CNSView } from './CNSRenderer';

const MAX_RECONNECT_DELAY_MS = 30_000;
const FLUX_SESSION_JSON_SUBPROTOCOL = 'flux-session-v1';

export interface SessionState {
  session_id: string;
  app_id: string;
  title: string;
  category: string;
  status: string;
  participants: string[];
  is_public: boolean;
  version: number;
  state_public: Record<string, unknown>;
  cns_view?: CNSView;
}

type SessionSocketMessage =
  | ({ type: 'state'; seq?: number } & SessionState)
  | { type: 'delete'; session_id: string; seq?: number }
  | { type: 'ping' };

export interface UseSessionStreamResult {
  publicState: Record<string, unknown>;
  cnsView: CNSView | null;
  title: string;
  status: string;
  participants: string[];
  loading: boolean;
  error: string | null;
}

function fluxWebSocketBaseUrl(): string {
  return FLUX_API_BASE.replace(/^https:/, 'wss:').replace(/^http:/, 'ws:');
}

function buildSessionWebSocketUrl(sessionId: string, lastSeq: number): string {
  const params = new URLSearchParams();
  if (lastSeq > 0) params.set('last_seq', String(lastSeq));
  const query = params.toString();
  return `${fluxWebSocketBaseUrl()}/api/session/ws/${sessionId}${query ? `?${query}` : ''}`;
}

type SessionWebSocketProtocolConfig = {
  protocols: string[];
  hasBearerToken: boolean;
};

async function waitForInitialAuthState(): Promise<void> {
  const maybeAuth = auth as typeof auth & { authStateReady?: () => Promise<void> };
  if (typeof maybeAuth.authStateReady === 'function') {
    await maybeAuth.authStateReady();
  }
}

async function buildSessionWebSocketProtocols(): Promise<SessionWebSocketProtocolConfig> {
  await waitForInitialAuthState();
  const protocols = [FLUX_SESSION_JSON_SUBPROTOCOL];
  const user = auth.currentUser;
  if (!user) {
    return { protocols, hasBearerToken: false };
  }
  try {
    const token = await user.getIdToken();
    if (token) {
      protocols.push(`bearer.${token}`);
      return { protocols, hasBearerToken: true };
    }
  } catch {
    // Public Flux sessions can still stream without actor-specific state.
  }
  return { protocols, hasBearerToken: false };
}

export function useSessionStream(sessionId: string | null): UseSessionStreamResult {
  const [publicState, setPublicState] = useState<Record<string, unknown>>({});
  const [cnsView, setCnsView] = useState<CNSView | null>(null);
  const [title, setTitle] = useState('');
  const [status, setStatus] = useState('');
  const [participants, setParticipants] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const lastSeqRef = useRef(0);

  useEffect(() => {
    lastSeqRef.current = 0;
    setPublicState({});
    setCnsView(null);
    setTitle('');
    setStatus('');
    setParticipants([]);
    setError(null);

    if (!sessionId) {
      setLoading(false);
      setError('No session ID in URL');
      return;
    }
    const activeSessionId = sessionId;
    setLoading(true);

    let socket: WebSocket | null = null;
    let reconnectTimeout: ReturnType<typeof setTimeout> | null = null;
    let reconnectAttempt = 0;
    let stopped = false;
    let receivedSnapshot = false;

    function applyState(state: SessionState) {
      setPublicState(state.state_public ?? {});
      if (state.cns_view && typeof state.cns_view === 'object' && 'type' in state.cns_view) {
        setCnsView(state.cns_view);
      }
      setTitle(state.title ?? '');
      setStatus(state.status ?? '');
      setParticipants(state.participants ?? []);
      setLoading(false);
      setError(null);
    }

    async function fallbackToREST() {
      try {
        const resp = await fetch(`${FLUX_API_BASE}/api/session/${encodeURIComponent(activeSessionId)}`);
        if (!resp.ok) {
          setError(resp.status === 404 ? 'Session not found' : `Error: ${resp.status}`);
          setLoading(false);
          return;
        }
        const data = await resp.json();
        setTitle(data.title ?? '');
        setStatus(data.status ?? '');
        setParticipants(data.participants ?? []);
        if (data.state && typeof data.state === 'object') {
          setPublicState(data.state as Record<string, unknown>);
        }
        if (data.view && typeof data.view === 'object' && 'type' in data.view) {
          setCnsView(data.view as CNSView);
        }
        setLoading(false);
        setError(null);
      } catch {
        if (stopped) return;
        setError('Failed to load session');
        setLoading(false);
      }
    }

    const scheduleReconnect = () => {
      if (stopped) return;
      const delay = Math.min(1000 * Math.pow(2, reconnectAttempt), MAX_RECONNECT_DELAY_MS);
      reconnectAttempt += 1;
      reconnectTimeout = setTimeout(() => {
        reconnectTimeout = null;
        void connectWebSocket();
      }, delay);
    };

    const connectWebSocket = async () => {
      if (stopped) return;

      let protocolConfig: SessionWebSocketProtocolConfig;
      try {
        protocolConfig = await buildSessionWebSocketProtocols();
      } catch {
        protocolConfig = { protocols: [FLUX_SESSION_JSON_SUBPROTOCOL], hasBearerToken: false };
      }
      if (stopped) return;

      let ws: WebSocket;
      try {
        ws = new WebSocket(buildSessionWebSocketUrl(activeSessionId, lastSeqRef.current), protocolConfig.protocols);
      } catch {
        setLoading(false);
        await fallbackToREST();
        if (protocolConfig.hasBearerToken) {
          scheduleReconnect();
        }
        return;
      }
      socket = ws;
      let receivedMessageOnSocket = false;

      ws.onopen = () => {
        reconnectAttempt = 0;
      };

      ws.onmessage = (event: MessageEvent<string>) => {
        receivedMessageOnSocket = true;
        if (typeof event.data !== 'string') {
          return;
        }
        let message: SessionSocketMessage;
        try {
          message = JSON.parse(event.data) as SessionSocketMessage;
        } catch {
          return;
        }

        if (message.type === 'state') {
          receivedSnapshot = true;
          applyState(message);
          if (typeof message.seq === 'number' && message.seq > lastSeqRef.current) {
            lastSeqRef.current = message.seq;
          }
          return;
        }

        if (message.type === 'delete') {
          setError('Session has been deleted');
          setLoading(false);
          stopped = true;
          ws.close();
        }
      };

      ws.onerror = () => {
        // Close owns reconnect behavior.
      };

      ws.onclose = () => {
        if (socket === ws) {
          socket = null;
        }
        if (stopped) {
          return;
        }
        if (!receivedSnapshot) {
          void fallbackToREST();
        }
        if (!protocolConfig.hasBearerToken && !receivedMessageOnSocket) {
          return;
        }
        scheduleReconnect();
      };
    };

    void connectWebSocket();

    return () => {
      stopped = true;
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout);
      }
      socket?.close();
      socket = null;
    };
  }, [sessionId]);

  return { publicState, cnsView, title, status, participants, loading, error };
}
